
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Swords,
  Users,
  Trophy,
  Map,
  Sparkles,
  Crown,
  Zap,
  Shield,
  Target,
  Skull,
  BookOpen,
  Castle,
  DoorOpen,
  Globe,
  Coins,
  Flame,
  ArrowRight
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API_URL = (import.meta.env.VITE_API_URL || "http://localhost:3001") + "/api/admin";

// Icon and color pools used when API returns features beyond the hardcoded defaults
const ICON_POOL = [Swords, Users, Crown, Trophy, DoorOpen, Globe, Castle, Coins, Flame, Map, Target, Sparkles, Shield, Zap, BookOpen, Skull];
const COLOR_POOL = [
  "from-red-900/50 to-red-950/50",
  "from-purple-900/50 to-purple-950/50",
  "from-yellow-900/50 to-yellow-950/50",
  "from-blue-900/50 to-blue-950/50",
  "from-indigo-900/50 to-indigo-950/50",
  "from-teal-900/50 to-teal-950/50",
  "from-green-900/50 to-green-950/50",
  "from-amber-900/50 to-amber-950/50",
  "from-slate-900/50 to-slate-950/50",
  "from-pink-900/50 to-pink-950/50",
  "from-cyan-900/50 to-cyan-950/50",
  "from-orange-900/50 to-orange-950/50",
];

const DEFAULT_FEATURES = [
  {
    icon: Swords,
    title: "Intense PvP Combat",
    description: "Our bread and butter. A combat system designed by PvP enthusiasts, for PvP enthusiasts. Every fight matters, every decision counts.",
    color: "from-red-900/50 to-red-950/50",
    detailedDescription: "Knight Arena's combat system is built from the ground up with PvP in mind. Every ability, every stat, and every mechanic is designed to create intense, skill-based combat where the better player wins. No auto-targeting, no pay-to-win advantages - just pure skill and strategy.",
    image: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=1200",
    highlights: ["Skill-based combat with high skill ceiling", "No auto-targeting - aim and precision matter", "Combo system rewards mastery", "Counterplay mechanics for every situation"]
  },
  {
    icon: Users,
    title: "Four Unique Classes",
    description: "Rogue, Warlord, Cleric, and Wizard - each with multiple specializations and playstyles. Master your class, dominate the arena.",
    color: "from-purple-900/50 to-purple-950/50",
    detailedDescription: "Choose from four distinct classes, each with their own identity and multiple specialization paths. Each class has 3-4 specialization trees that dramatically change your playstyle.",
    image: "https://images.unsplash.com/photo-1509114397022-ed747cca3f65?w=1200",
    highlights: ["Rogue: Stealth, burst damage, mobility", "Warlord: Tankiness, crowd control, damage", "Cleric: Healing, buffs, holy damage", "Wizard: AoE damage, crowd control, utility"]
  },
  {
    icon: Crown,
    title: "Political System",
    description: "Navigate a complex web of alliances, rivalries, and power struggles. Influence the realm through politics, not just combat.",
    color: "from-yellow-900/50 to-yellow-950/50",
    detailedDescription: "The political system allows players to engage in diplomacy, form alliances, and manipulate power structures. Join councils, vote on faction decisions, and support candidates for leadership positions.",
    image: "https://images.unsplash.com/photo-1531243269054-5ebf6f34081e?w=1200",
    highlights: ["Council voting and faction decisions", "Support or oppose leadership candidates", "Form political alliances and betrayals", "Influence nation-wide policies"]
  },
  {
    icon: Trophy,
    title: "National Points & Arena",
    description: "Prove your worth in ranked PvP. Earn National Points and Arena Points to climb leaderboards and gain legendary status.",
    color: "from-blue-900/50 to-blue-950/50",
    detailedDescription: "Compete in ranked PvP to earn National Points and Arena Points. These points determine your standing on the leaderboards and unlock exclusive rewards.",
    image: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=1200",
    highlights: ["Ranked PvP with seasonal rewards", "National Points from open-world PvP", "Arena Points from structured matches", "Global and faction leaderboards"]
  },
  {
    icon: DoorOpen,
    title: "Dungeons",
    description: "Challenging PvE content with unique mechanics and valuable rewards. Perfect your group coordination and strategy.",
    color: "from-indigo-900/50 to-indigo-950/50",
    detailedDescription: "Dungeons offer challenging PvE content that requires coordination, strategy, and mastery of your class. Each dungeon features unique boss mechanics, environmental hazards, and puzzles.",
    image: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=1200",
    highlights: ["Complex boss mechanics requiring teamwork", "Multiple difficulty tiers", "Exclusive gear and crafting materials", "Speedrun leaderboards"]
  },
  {
    icon: Globe,
    title: "World States",
    description: "The world evolves based on player actions. Control territories, trigger events, and shape the realm's future.",
    color: "from-teal-900/50 to-teal-950/50",
    detailedDescription: "World States create a living, breathing world that changes based on player actions. Factions can control territories, trigger world events, and influence global conditions.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200",
    highlights: ["Dynamic territory control", "Player-triggered world events", "Faction dominance affects world conditions", "Persistent world changes between sessions"]
  },
  {
    icon: Users,
    title: "Clans",
    description: "Form powerful guilds with shared goals. Build clan halls, manage resources, and dominate through collective strength.",
    color: "from-green-900/50 to-green-950/50",
    detailedDescription: "Clans are more than just groups - they're organized communities with their own infrastructure. Build and upgrade clan halls, manage shared resources, and organize events.",
    image: "https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=1200",
    highlights: ["Build and upgrade clan halls", "Shared clan bank and resources", "Customizable ranks and permissions", "Scheduled clan vs clan battles"]
  },
  {
    icon: Castle,
    title: "Castle Siege War",
    description: "Epic large-scale battles for strategic strongholds. Coordinate attacks, defend walls, and claim castles for your faction.",
    color: "from-red-900/50 to-red-950/50",
    detailedDescription: "Castle Sieges are massive PvP events where factions battle for control of strategic strongholds. Attackers must breach walls, overcome defenses, and capture the throne room.",
    image: "https://images.unsplash.com/photo-1536859975388-b404e674df33?w=1200",
    highlights: ["Massive 50v50+ battles", "Siege weapons and defensive structures", "Castle ownership grants economic benefits", "Scheduled events with faction-wide impact"]
  },
  {
    icon: Coins,
    title: "Player-Driven Economy",
    description: "A true free market where supply and demand rule. Craft, trade, and build your fortune through economic mastery.",
    color: "from-amber-900/50 to-amber-950/50",
    detailedDescription: "The economy in Knight Arena is entirely player-driven. Every item, resource, and piece of gear can be traded. Prices fluctuate based on supply and demand.",
    image: "https://images.unsplash.com/photo-1607863680198-23d4b2565df0?w=1200",
    highlights: ["No fixed vendor prices - free market", "Player-run shops and auction houses", "Supply and demand driven pricing", "Crafting and gathering professions"]
  },
  {
    icon: Flame,
    title: "World Bosses",
    description: "Face legendary creatures that require faction-wide coordination. Compete for loot and glory against other factions.",
    color: "from-purple-900/50 to-purple-950/50",
    detailedDescription: "World Bosses are massive threats that spawn in open-world zones, requiring dozens of coordinated players to defeat. Multiple factions can fight simultaneously, creating chaotic PvPvE battles.",
    image: "https://images.unsplash.com/photo-1589308078059-be1415eab4c3?w=1200",
    highlights: ["Requires 20-40+ coordinated players", "PvPvE - fight bosses and rival factions", "Legendary loot and crafting materials", "Dynamic difficulty based on participants"]
  },
  {
    icon: Map,
    title: "Diverse Zones",
    description: "From dark forests to contested battlegrounds. Each zone has its own dangers, treasures, and opportunities for PvP encounters.",
    color: "from-slate-900/50 to-slate-950/50",
    detailedDescription: "Explore a variety of zones, each with unique themes, dangers, and opportunities. From safe zones where you can rest and trade, to high-risk zones with the best rewards.",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200",
    highlights: ["Safe zones for trading and rest", "Contested territories with constant PvP", "High-risk zones with best rewards", "Hidden areas and secrets to discover"]
  },
  {
    icon: Target,
    title: "Assassination Contracts",
    description: "Take out high-value targets for rewards. Or become the target yourself. In Knight Arena, everyone has a price.",
    color: "from-red-900/50 to-red-950/50",
    detailedDescription: "The assassination system allows players to place bounties on enemies or accept contracts to hunt down targets. Successfully completing contracts earns gold and reputation.",
    image: "https://images.unsplash.com/photo-1563089145-599997674d42?w=1200",
    highlights: ["Place bounties on enemy players", "Accept assassination contracts", "Track targets across the world", "Notoriety system for frequent assassins"]
  },
  {
    icon: Sparkles,
    title: "Talent System",
    description: "Customize your character with deep talent trees. Make meaningful choices that define your playstyle and combat identity.",
    color: "from-pink-900/50 to-pink-950/50",
    detailedDescription: "The talent system offers deep customization through multiple talent trees per class. Each choice is meaningful and affects your playstyle significantly.",
    image: "https://images.unsplash.com/photo-1614732414444-096e5f1122d5?w=1200",
    highlights: ["Multiple talent trees per class", "Meaningful choices that affect playstyle", "Mix and match from different trees", "Respec system for experimentation"]
  },
  {
    icon: Shield,
    title: "Strategic Factions",
    description: "Choose between House Kalivant, House Drakonis, or the Outlaws. Each faction offers unique opportunities and challenges.",
    color: "from-cyan-900/50 to-cyan-950/50",
    detailedDescription: "Three factions vie for control of the realm. Each faction has unique questlines, territories, and philosophies that affect your relationships and available content.",
    image: "https://images.unsplash.com/photo-1533310266094-8898a03807dd?w=1200",
    highlights: ["Three unique factions with distinct identities", "Faction-specific questlines and content", "Territory control and faction wars", "Faction reputation and ranks"]
  },
  {
    icon: Zap,
    title: "Combo Mechanics",
    description: "Chain abilities for devastating effects. High skill ceiling, rewarding mastery. Button mashing won't save you here.",
    color: "from-orange-900/50 to-orange-950/50",
    detailedDescription: "Master the art of chaining abilities to create devastating combos. Certain ability sequences trigger bonus effects, additional damage, or crowd control extensions.",
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1200",
    highlights: ["Ability chaining system", "Bonus effects from proper sequencing", "Class-specific combo paths", "Mastery is visibly rewarding"]
  },
  {
    icon: BookOpen,
    title: "Expertise System",
    description: "Master skills like Foraging, Prospecting, Crafting, and more. Become self-sufficient or specialize in profitable trades.",
    color: "from-teal-900/50 to-teal-950/50",
    detailedDescription: "Develop non-combat skills through the expertise system. Gather resources, craft powerful items, or become a master merchant.",
    image: "https://images.unsplash.com/photo-1515694590379-b5c90da73347?w=1200",
    highlights: ["Multiple expertise paths to master", "Foraging, mining, herbalism, and more", "Crafting system with quality tiers", "Trade and merchant systems"]
  },
  {
    icon: Skull,
    title: "Dark & Gritty",
    description: "Not another colorful theme park MMO. Knight Arena is brutal, unforgiving, and rewards only those who prove themselves worthy.",
    color: "from-gray-900/50 to-gray-950/50",
    detailedDescription: "Knight Arena embraces a dark, gritty aesthetic and unforgiving gameplay. Actions have consequences, death matters, and reputation is earned through blood and sweat.",
    image: "https://images.unsplash.com/photo-1533135091724-62cc5402aa20?w=1200",
    highlights: ["Dark medieval aesthetic", "Consequences for death and failure", "Hardcore PvP with full loot in zones", "Reputation system that matters"]
  }
];

export default function Features() {
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [heroContent, setHeroContent] = useState({
    title: "Game Features",
    subtitle: "A PvP-focused MMORPG where combat is king, reputation is earned, and only the worthy become legends."
  });
  const [features, setFeatures] = useState(DEFAULT_FEATURES);

  useEffect(() => {
    fetch(`${API_URL}/content/features`)
      .then(r => r.json())
      .then(data => {
        const c = data.content || {};
        if (c.hero) {
          setHeroContent(h => ({ ...h, ...c.hero }));
        }
        if (Array.isArray(c.features) && c.features.length > 0) {
          // Merge API features with defaults: keep icon/color/details from default by index position
          const merged = c.features.map((feat, idx) => {
            const def = DEFAULT_FEATURES[idx] || {
              icon: ICON_POOL[idx % ICON_POOL.length],
              color: COLOR_POOL[idx % COLOR_POOL.length],
            };
            return { ...def, title: feat.title, description: feat.description };
          });
          setFeatures(merged);
        }
      })
      .catch(() => {}); // silent fail, keep defaults
  }, []);

  return (
    <div className="min-h-screen relative">
      {/* Background - Fixed position */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/2878f0ffc_loading_screen_09.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 py-24">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">
                {heroContent.title}
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {heroContent.subtitle}
            </p>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card
                key={idx}
                onClick={() => setSelectedFeature(feature)}
                className={`bg-gradient-to-b ${feature.color} backdrop-blur-sm border-[#8B0000]/30 p-8 hover:border-white/50 transition-all duration-300 hover:scale-105 group cursor-pointer`}
              >
                <feature.icon className="w-12 h-12 text-white mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-white transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>

        {/* Feature Detail Modal */}
        <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
          <DialogContent className="max-w-3xl bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-[#8B0000]/30 text-white max-h-[85vh] overflow-y-auto">
            {selectedFeature && (
              <>
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                    <selectedFeature.icon className="w-8 h-8" />
                    {selectedFeature.title}
                  </DialogTitle>
                </DialogHeader>

                {/* Feature Image */}
                {selectedFeature.image && (
                  <div className="w-full aspect-video rounded-lg overflow-hidden mb-4 bg-black max-h-[300px]">
                    <img
                      src={selectedFeature.image}
                      alt={selectedFeature.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Description */}
                <DialogDescription className="text-gray-300 text-base leading-relaxed mb-4">
                  {selectedFeature.detailedDescription || selectedFeature.description}
                </DialogDescription>

                {/* Highlights */}
                {selectedFeature.highlights && selectedFeature.highlights.length > 0 && (
                  <div className="bg-[#0a0a0a]/50 border border-[#8B0000]/30 rounded-lg p-5">
                    <h4 className="text-lg font-bold text-white mb-3">Key Features</h4>
                    <ul className="space-y-2">
                      {selectedFeature.highlights.map((highlight, i) => (
                        <li key={i} className="flex items-start gap-3 text-gray-300 text-sm">
                          <div className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Bottom CTA - Wiki Link */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border border-[#8B0000]/30 rounded-lg p-12">
            <BookOpen className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Want to Learn More?
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Dive deeper into the world of Knight Arena. Our Wiki contains detailed information about game mechanics, lore, classes, and more.
            </p>
            <Link to={createPageUrl("Wiki")}>
              <Button size="lg" className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white border-none px-8 py-6 text-lg">
                Explore the Wiki
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
